/**
 * "New project" CTA. Opens FormDialog → react-hook-form → POST /v1/projects,
 * then navigates to the new project's detail page.
 */
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { FolderPlus } from 'lucide-react';
import { toast } from 'sonner';
import type { CreateProjectRequest, Project } from '@seo/types';
import { Button, type ButtonProps } from '@/components/ui/button';
import { FormDialog } from '@/components/ui/form-dialog';
import { TextAreaField, TextField } from '@/components/ui/form-field';
import { FormField } from '@/components/ui/form-field';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { summarizeApiError } from '@/lib/api';
import { useCreateProject } from '@/features/projects/use-create-project';

const COUNTRY_OPTIONS = [
  { code: 'US', label: 'United States' },
  { code: 'GB', label: 'United Kingdom' },
  { code: 'IN', label: 'India' },
  { code: 'DE', label: 'Germany' },
  { code: 'CA', label: 'Canada' },
  { code: 'AU', label: 'Australia' },
] as const;

const LANGUAGE_OPTIONS = [
  { code: 'en', label: 'English' },
  { code: 'hi', label: 'Hindi' },
  { code: 'de', label: 'German' },
  { code: 'fr', label: 'French' },
  { code: 'es', label: 'Spanish' },
] as const;

const DOMAIN_LIKE = /^(https?:\/\/)?([a-z0-9-]+(\.[a-z0-9-]+)+)(\/.*)?$/i;
const ProjectFormSchema = z.object({
  name: z.string().trim().min(1, 'Required').max(120, 'Too long'),
  domain: z
    .string()
    .trim()
    .min(1, 'Required')
    .regex(DOMAIN_LIKE, 'Looks like that is not a valid domain'),
  brand_positioning: z.string().max(1000, 'Keep under 1000 characters').optional(),
  country_code: z.string().length(2),
  language: z.string().regex(/^[a-z]{2,3}$/),
});
type ProjectFormValues = z.infer<typeof ProjectFormSchema>;

export interface ProjectCreateButtonProps {
  label?: string;
  buttonProps?: Omit<ButtonProps, 'onClick'>;
  onCreated?: (project: Project) => void;
  navigateOnSuccess?: boolean;
}

export function ProjectCreateButton({
  label = 'New project',
  buttonProps,
  onCreated,
  navigateOnSuccess = true,
}: ProjectCreateButtonProps): JSX.Element {
  const [open, setOpen] = useState(false);
  return (
    <FormDialogShell
      open={open}
      onOpenChange={setOpen}
      trigger={
        <Button {...buttonProps}>
          <FolderPlus className="h-4 w-4" />
          {label}
        </Button>
      }
      onCreated={onCreated}
      navigateOnSuccess={navigateOnSuccess}
    />
  );
}

function FormDialogShell({
  open,
  onOpenChange,
  trigger,
  onCreated,
  navigateOnSuccess,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  trigger: JSX.Element;
  onCreated?: (p: Project) => void;
  navigateOnSuccess: boolean;
}): JSX.Element {
  const navigate = useNavigate();
  const create = useCreateProject();
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<ProjectFormValues>({
    resolver: zodResolver(ProjectFormSchema),
    defaultValues: {
      name: '',
      domain: '',
      brand_positioning: '',
      country_code: 'US',
      language: 'en',
    },
  });

  const country = watch('country_code');
  const language = watch('language');

  const submit = handleSubmit(async (values) => {
    const positioning = values.brand_positioning?.trim();
    const payload: CreateProjectRequest = {
      name: values.name.trim(),
      domain: normalizeDomain(values.domain),
      ...(positioning ? { brand_positioning: positioning } : {}),
      country_code: values.country_code.toUpperCase(),
      language: values.language.toLowerCase(),
    };
    try {
      const project = await create.mutateAsync(payload);
      toast.success('Project created', { description: project.name });
      reset();
      onOpenChange(false);
      onCreated?.(project);
      if (navigateOnSuccess) {
        await navigate({
          to: '/projects/$projectId',
          params: { projectId: project.id },
        });
      }
    } catch (e) {
      const summary = summarizeApiError(e, {
        fallbackTitle: 'Could not create project',
        fieldLabels: {
          name: 'Name',
          domain: 'Domain',
          brand_positioning: 'Brand positioning',
          country_code: 'Country',
          language: 'Language',
        },
      });
      toast.error(summary.title, { description: summary.description });
    }
  });

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      trigger={trigger}
      title="New project"
      description="Each project represents one site you want to audit and create content for."
      submitLabel="Create project"
      submitting={create.isPending}
      onSubmit={submit}
    >
      <TextField
        label="Name"
        placeholder="Acme Marketing site"
        autoFocus
        error={errors.name?.message}
        {...register('name')}
      />
      <TextField
        label="Domain"
        placeholder="https://example.com"
        hint="Bare hostnames get auto-prefixed with https://."
        error={errors.domain?.message}
        {...register('domain')}
      />
      <TextAreaField
        label="Brand positioning"
        placeholder="e.g. Name.ai is a domain-name discovery tool for founders picking their first brand. We don't sell hosting, page builders, or logo design."
        hint="1-3 sentences describing what your brand sells, your unique wedge, and what you don't do. Prevents brief prompts from anchoring on competitor positioning."
        rows={4}
        error={errors.brand_positioning?.message}
        {...register('brand_positioning')}
      />
      <div className="grid grid-cols-2 gap-3">
        <FormField label="Country">
          <Select
            value={country}
            onValueChange={(v) => setValue('country_code', v, { shouldDirty: true })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {COUNTRY_OPTIONS.map((c) => (
                <SelectItem key={c.code} value={c.code}>
                  {c.code} — {c.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>
        <FormField label="Language">
          <Select
            value={language}
            onValueChange={(v) => setValue('language', v, { shouldDirty: true })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {LANGUAGE_OPTIONS.map((l) => (
                <SelectItem key={l.code} value={l.code}>
                  {l.code} — {l.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>
      </div>
    </FormDialog>
  );
}

function normalizeDomain(raw: string): string {
  const trimmed = raw.trim();
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}
